import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { CurrentUserViewModel } from '../types/auth/CurrentUserViewModel';
import type { UserUpdateModel } from '../types/auth/UserUpdateModel';
import { apiClient } from '../client/client';

/**
 * Payload shape used by the profile page (firstName, lastName, email, phone, about).
 * UserService maps this to UserUpdateModel (phone -> phoneNumber, about -> description).
 */
export interface UserProfileUpdatePayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  about?: string;
}

export interface UserSecurityUpdatePayload {
  password: string;
  email?: string;
  phoneNumber?: string;
}

export interface CreateAdminPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  isEmailConfirmed?: boolean;
}

/**
 * User service – profile update for the authenticated user.
 * Uses PUT /api/user/{id} with UserUpdateModel.
 */
export class UserService {
  private static readonly BASE = '/api/user';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  /**
   * Update user profile. Accepts profile-page shape (phone, about); maps to API (phoneNumber, description).
   */
  async updateUser(
    userId: string,
    payload: UserProfileUpdatePayload | UserUpdateModel
  ): Promise<ServiceResultT<CurrentUserViewModel | null>> {
    const body = this.toUpdateModel(payload);
    const { data } = await this.client.put<
      ServiceResultT<CurrentUserViewModel | null>
    >(`${UserService.BASE}/${userId}`, body);
    return data;
  }

  async updateUserSecurity(
    userId: string,
    payload: UserSecurityUpdatePayload
  ): Promise<ServiceResultT<CurrentUserViewModel | null>> {
    const { data } = await this.client.put<
      ServiceResultT<CurrentUserViewModel | null>
    >(`${UserService.BASE}/security-information/${userId}`, payload);
    return data;
  }

  /**
   * Get user by id (GET /api/user/{id}). Returns ServiceResult with user or null.
   */
  async getById(userId: string): Promise<ServiceResultT<CurrentUserViewModel | null>> {
    const { data } = await this.client.get<
      ServiceResultT<CurrentUserViewModel | null>
    >(`${UserService.BASE}/${userId}`);
    return data;
  }

  /**
   * Upload profile picture. POST /api/user/profile-picture with multipart/form-data (file).
   * On success, call auth/me or refreshUser to get updated profilePictureUrl.
   */
  async uploadProfilePicture(file: File): Promise<ServiceResultT<null>> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await this.client.post<ServiceResultT<null>>(
      `${UserService.BASE}/profile-picture`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return data;
  }

  /**
   * Delete profile picture. DELETE /api/user/profile-picture.
   * On success, call auth/me or refreshUser to clear profilePictureUrl.
   */
  async deleteProfilePicture(): Promise<ServiceResultT<null>> {
    const { data } = await this.client.delete<ServiceResultT<null>>(
      `${UserService.BASE}/profile-picture`
    );
    return data;
  }

  async createAdmin(payload: CreateAdminPayload): Promise<ServiceResultT<CurrentUserViewModel | null>> {
    const { data } = await this.client.post<ServiceResultT<CurrentUserViewModel | null>>(
      `${UserService.BASE}/create-admin`,
      payload
    );
    return data;
  }

  async createCompanyAdmin(payload: CreateAdminPayload): Promise<ServiceResultT<CurrentUserViewModel | null>> {
    const { data } = await this.client.post<ServiceResultT<CurrentUserViewModel | null>>(
      `${UserService.BASE}/create-company-admin`,
      payload
    );
    return data;
  }

  private toUpdateModel(
    payload: UserProfileUpdatePayload | UserUpdateModel
  ): UserUpdateModel {
    const hasProfileShape = 'phone' in payload || 'about' in payload;
    if (hasProfileShape) {
      const p = payload as UserProfileUpdatePayload;
      return {
        firstName: p.firstName ?? undefined,
        lastName: p.lastName ?? undefined,
        email: p.email ?? undefined,
        phoneNumber: p.phone ?? undefined,
        description: p.about ?? undefined,
      };
    }
    const p = payload as UserUpdateModel;
    return {
      firstName: p.firstName ?? undefined,
      lastName: p.lastName ?? undefined,
      email: p.email ?? undefined,
      phoneNumber: p.phoneNumber ?? undefined,
      description: p.description ?? undefined,
    };
  }
}

export const userService = new UserService();
